/**
 * Application configuration settings
 */

const metaEnv = (() => {
  try {
    return (import.meta as any).env || {};
  } catch (_error) {
    return {} as Record<string, string>;
  }
})();

const runningInBrowser = typeof window !== 'undefined';

const FALLBACK_BACKEND_PORT =
  (typeof process !== 'undefined' && process.env.VITE_BACKEND_PORT) || metaEnv.VITE_BACKEND_PORT || '3013';

function resolveBackendApiUrl() {
  if (runningInBrowser) {
    // Use same-origin requests when in the browser so Vite/Express can proxy without CORS.
    return '';
  }

  if (typeof process !== 'undefined') {
    if (process.env.API_BASE_URL) return process.env.API_BASE_URL;
    if (process.env.VITE_API_URL) return process.env.VITE_API_URL;
    if (process.env.NEXT_PUBLIC_API_URL) return process.env.NEXT_PUBLIC_API_URL;
  }

  if (metaEnv.VITE_API_URL) {
    return metaEnv.VITE_API_URL;
  }

  return `http://localhost:${FALLBACK_BACKEND_PORT}`;
}

// API configuration
export const API_CONFIG = {
  // Base URL for backend API endpoints
  backendApiUrl: resolveBackendApiUrl(),
  
  // Payment processing endpoints
  paymentApi: {
    donationProcess: '/api/nmi/donation-process',
    paymentProcess: '/api/nmi/process',
    vault: '/api/nmi/vault',
  },
  
  // External API URLs
  externalApis: {
    nmiTransact: 'https://secure.nmi.com/api/transact.php',
    nmiCollectJs: 'https://secure.nmi.com/token/Collect.js',
  }
};

/**
 * Get the full URL for a backend API endpoint
 * @param endpoint The API endpoint path
 * @returns The complete URL for the API endpoint
 */
export function getApiUrl(endpoint: string): string {
  // If endpoint is already a complete URL, return it
  if (endpoint.startsWith('http')) {
    return endpoint;
  }
  
  // Make sure endpoint starts with a slash
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  
  // Return the complete URL
  return `${API_CONFIG.backendApiUrl}${normalizedEndpoint}`;
}

/**
 * Get the URL for a payment API endpoint
 * @param endpoint The payment endpoint key from API_CONFIG.paymentApi
 * @returns The complete URL for the payment API endpoint
 */
export function getPaymentApiUrl(endpoint: keyof typeof API_CONFIG.paymentApi): string {
  return getApiUrl(API_CONFIG.paymentApi[endpoint]);
}

export const config = {
  nmi: {
    securityKey: undefined, // Security keys should only be fetched from the database per company
    checkoutUrl: 'https://secure.networkmerchants.com/checkout/pay.php'
  }
} as const;

// Add this helper function to make it clear that direct access isn't allowed
export function getNMISecurityKey(): never {
  throw new Error(
    'SECURITY ERROR: NMI security keys must be fetched from the database per company. ' +
    'Using a global security key is not allowed for security reasons.'
  );
} 
